import React, { useMemo, useState } from "react";
import { Box, Button } from "@mui/material";

import { EsgSingleSelectControlledField } from "components";
import { ChangeUtilityBillsStatusesRequestDTO } from "api";

import { useBulkUtilityBillsStatuses } from "hooks";
import { UTILITY_BILL_STATUS_OPTIONS } from "../../consts";

type UtilityBillsBulkStatusBarProps = {
  selectedBillIds: string[];
  selectedCount: number;
  disabled?: boolean;
  onClear: () => void;
  showReviewed?: boolean;
  showNotApplicable?: boolean;
};

const buildDot = (dotColor: string) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      backgroundColor: dotColor,
      flex: "0 0 auto",
    }}
  />
);

export const UtilityBillsBulkStatusBar = ({
  selectedBillIds,
  selectedCount,
  disabled = false,
  onClear,
  showReviewed = false,
  showNotApplicable = false,
}: UtilityBillsBulkStatusBarProps) => {
  const [targetStatus, setTargetStatus] = useState<string>("");

  const dropdownOptions = useMemo(
    () =>
      UTILITY_BILL_STATUS_OPTIONS
        .filter((s) => s.id !== "Reviewed" || showReviewed)
        .filter((s) => !s.id.startsWith("Not Applicable") || showNotApplicable)
        .map((s) => ({
          label: s.label,
          value: s.id,
          icon: buildDot(s.dotColor),
        })),
    [showReviewed, showNotApplicable],
  );

  const { applyBulkStatuses, isApplying } = useBulkUtilityBillsStatuses(() => {
    // Reset local UI after success (selection itself is cleared by parent)
    setTargetStatus("");
  });

  const isApplyDisabled = disabled || isApplying || selectedBillIds.length === 0 || !targetStatus;

  const onApply = async () => {
    if (isApplyDisabled) return;

    const payload: ChangeUtilityBillsStatusesRequestDTO = {
      billIds: selectedBillIds,
      targetStatus,
    };

    try {
      await applyBulkStatuses(payload);
      onClear();
    } catch {
      // Errors are notified by the hook
    }
  };

  const onClearClick = () => {
    setTargetStatus("");
    onClear();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        padding: "10px 16px",
        mb: 2,
        backgroundColor: "#e5f6fd",
        borderRadius: "7px",
        width: "100%",
      }}
    >
      <Box sx={{ color: "#054254", fontWeight: 500 }}>
        {`${selectedCount} entries selected`}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <EsgSingleSelectControlledField
          selectedValue={targetStatus}
          setSelectedValue={(value) => setTargetStatus(String(value))}
          options={dropdownOptions}
          placeholder="Set status"
          disabled={disabled || isApplying}
          width="220px"
          height="36px"
        />

        <Button
          variant="contained"
          sx={{ borderRadius: "8px", height: "36px" }}
          disabled={isApplyDisabled}
          onClick={onApply}
        >
          Apply
        </Button>

        <Button
          variant="contained"
          sx={{ borderRadius: "8px", height: "36px" }}
          disabled={disabled || isApplying}
          onClick={onClearClick}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};
